import { Alert, Skeleton, Stack } from '@mui/material';
import { Fragment } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import {
  SidePanelPersonalizationSection,
  usePersonalizedSidePanel,
} from 'src/graphql/entities/side-panel-personalization';
import { SectionHeader } from 'src/pages/Conversations/SidePanel/section-header';
import { getDisplayedRowClusters } from './cluster-rows';
import { CollapsibleGroup } from './collapsible-group';

const SectionErrorFallback = () => (
  <Alert severity="error" sx={{ fontSize: '0.75rem', mx: 2 }}>
    Unable to display properties.
  </Alert>
);

const SkeletonRows = () => (
  <Stack gap={0.5} px={2} py={0.5}>
    {[35, 45, 55].map((valueWidth) => (
      <Stack key={valueWidth} direction="row" gap={1} alignItems="center">
        <Skeleton width="35%" height={16} />
        <Skeleton width={`${valueWidth}%`} height={16} />
      </Stack>
    ))}
  </Stack>
);

const PersonalizedSection = ({
  surfaceKey,
  section,
  hideEmpty,
}: {
  surfaceKey: string;
  section: SidePanelPersonalizationSection;
  hideEmpty: boolean;
}) => {
  const clusters = getDisplayedRowClusters(section.properties, hideEmpty);

  if (clusters.length === 0 && !section.loading && !section.notice) {
    return null;
  }

  return (
    <Stack gap={0.5} role="region" aria-label={`${section.label} properties`}>
      {section.hidePanelLabel ? null : <SectionHeader title={section.label} />}
      {clusters.map((cluster) =>
        cluster.group ? (
          <CollapsibleGroup
            key={cluster.rows[0].key}
            scopeKey={`side-panel:${surfaceKey}:${section.key}`}
            groupKey={cluster.group.key}
            label={cluster.group.label}
          >
            {cluster.rows.map((descriptor) => (
              <descriptor.Row key={descriptor.key} propertyKey={descriptor.key} />
            ))}
          </CollapsibleGroup>
        ) : (
          <Fragment key={cluster.rows[0].key}>
            {cluster.rows.map((descriptor) => (
              <descriptor.Row key={descriptor.key} propertyKey={descriptor.key} />
            ))}
          </Fragment>
        ),
      )}
      {section.loading ? <SkeletonRows /> : null}
      {section.notice ? (
        <Alert severity="warning" sx={{ fontSize: '0.75rem', mx: 2 }}>
          {section.notice}
        </Alert>
      ) : null}
    </Stack>
  );
};

export const PersonalizedSections = () => {
  const {
    surface,
    viewModel: { sections, hideEmpty, loading },
  } = usePersonalizedSidePanel();

  return (
    <ErrorBoundary FallbackComponent={SectionErrorFallback}>
      <Stack gap={2}>
        {loading ? (
          <SkeletonRows />
        ) : (
          sections.map((section) => (
            <PersonalizedSection
              key={section.key}
              surfaceKey={surface.surfaceKey}
              section={section}
              hideEmpty={hideEmpty}
            />
          ))
        )}
      </Stack>
    </ErrorBoundary>
  );
};
